import { useQuery } from "@tanstack/react-query";
import { ReactNode, createContext, useContext } from "react";
import { tagService } from "../../../admin/src/shared/api";
import { eventCategoryService } from "../../api/eventCategoryService";
import { eventTypeService } from "../../api/eventTypeService";
import { industryService } from "../../api/industryService";
import { locationService } from "../../api/locationService";
import { organizationalUnitService } from "../../api/organizationalUnitService";
import { organizerService } from "../../api/organizerService";
import { regionService } from "../../api/regionService";
import { targetGroupService } from "../../api/targetGroupService";
import { EventCategoryOptionResource } from "../../types/eventCategory";
import { EventTypeOptionResource } from "../../types/eventType";
import { IndustryOptionResource } from "../../types/industry";
import { LocationOptionResource } from "../../types/location";
import { OrganizationalUnitOptionResource } from "../../types/organizationalUnit";
import { OrganizerOptionResource } from "../../types/organizer";
import { RegionOptionResource } from "../../types/region";
import { TagOptionResource } from "../../types/tag";
import { TargetGroupOptionResource } from "../../types/targetGroup";

interface EventFilterAccordionItemsState {
	eventTypes: EventTypeOptionResource[] | undefined;
	eventTypesIsLoading: boolean;
	renderEventTypesAsCheckboxes: boolean | undefined;
	categories: EventCategoryOptionResource[] | undefined;
	categoriesIsLoading: boolean;
	renderCategoriesAsCheckboxes: boolean | undefined;
	industries: IndustryOptionResource[] | undefined;
	industriesIsLoading: boolean;
	renderIndustriesAsCheckboxes: boolean | undefined;
	locations: LocationOptionResource[] | undefined;
	locationsIsLoading: boolean;
	renderLocationsAsCheckboxes: boolean | undefined;
	organizationalUnits: OrganizationalUnitOptionResource[] | undefined;
	organizationalUnitsIsLoading: boolean;
	renderOrganizationalUnitsAsCheckboxes: boolean | undefined;
	organizers: OrganizerOptionResource[] | undefined;
	organizersIsLoading: boolean;
	renderOrganizersAsCheckboxes: boolean | undefined;
	regions: RegionOptionResource[] | undefined;
	regionsIsLoading: boolean;
	renderRegionsAsCheckboxes: boolean | undefined;
	tags: TagOptionResource[] | undefined;
	tagsIsLoading: boolean;
	renderTagsAsCheckboxes: boolean | undefined;
	targetGroups: TargetGroupOptionResource[] | undefined;
	targetGroupsIsLoading: boolean;
	renderTargetGroupsAsCheckboxes: boolean | undefined;
}

const EventFilterAccordionItemsContext = createContext<EventFilterAccordionItemsState | undefined>(undefined);

interface Props {
	children: ReactNode;
}

export function EventFilterAccordionItemsProvider({ children }: Props) {
	const { data: eventTypesResponse, isLoading: eventTypesIsLoading } = useQuery({
		queryFn: () => eventTypeService.options("", 1, { perPage: 15 }).then((x) => x.data),
		queryKey: eventTypeService.quickFilterKey,
	});

	const { data: categoriesResponse, isLoading: categoriesIsLoading } = useQuery({
		queryFn: () => eventCategoryService.options("", 1, { perPage: 15 }).then((x) => x.data),
		queryKey: eventCategoryService.quickFilterKey,
	});

	const { data: industriesResponse, isLoading: industriesIsLoading } = useQuery({
		queryFn: () => industryService.options("", 1, { perPage: 15 }).then((x) => x.data),
		queryKey: industryService.quickFilterKey,
	});

	const { data: locationsResponse, isLoading: locationsIsLoading } = useQuery({
		queryFn: () => locationService.options("", 1, { perPage: 15 }).then((x) => x.data),
		queryKey: locationService.quickFilterKey,
	});

	const { data: organizationalUnitsResponse, isLoading: organizationalUnitsIsLoading } = useQuery({
		queryFn: () => organizationalUnitService.options("", 1, { perPage: 15 }).then((x) => x.data),
		queryKey: organizationalUnitService.quickFilterKey,
	});

	const { data: organizersResponse, isLoading: organizersIsLoading } = useQuery({
		queryFn: () => organizerService.options("", 1, { perPage: 15 }).then((x) => x.data),
		queryKey: organizerService.quickFilterKey,
	});

	const { data: regionsResponse, isLoading: regionsIsLoading } = useQuery({
		queryFn: () => regionService.options("", 1, { perPage: 15 }).then((x) => x.data),
		queryKey: regionService.quickFilterKey,
	});

	const { data: tagsResponse, isLoading: tagsIsLoading } = useQuery({
		queryFn: () => tagService.options("", 1, { perPage: 15 }).then((x) => x.data),
		queryKey: tagService.quickFilterKey,
	});

	const { data: targetGroupsResponse, isLoading: targetGroupsIsLoading } = useQuery({
		queryFn: () => targetGroupService.options("", 1, { perPage: 15 }).then((x) => x.data),
		queryKey: targetGroupService.quickFilterKey,
	});

	return (
		<EventFilterAccordionItemsContext.Provider
			value={{
				eventTypes: eventTypesResponse?.data,
				eventTypesIsLoading,
				renderEventTypesAsCheckboxes: eventTypesResponse?.meta && eventTypesResponse.meta.total < 15,
				categories: categoriesResponse?.data,
				categoriesIsLoading,
				renderCategoriesAsCheckboxes: categoriesResponse?.meta && categoriesResponse.meta.total < 15,
				industries: industriesResponse?.data,
				industriesIsLoading,
				renderIndustriesAsCheckboxes: industriesResponse?.meta && industriesResponse.meta.total < 15,
				locations: locationsResponse?.data,
				locationsIsLoading,
				renderLocationsAsCheckboxes: locationsResponse?.meta && locationsResponse.meta.total < 15,
				organizationalUnits: organizationalUnitsResponse?.data,
				organizationalUnitsIsLoading,
				renderOrganizationalUnitsAsCheckboxes:
					organizationalUnitsResponse?.meta && organizationalUnitsResponse.meta.total < 15,
				organizers: organizersResponse?.data,
				organizersIsLoading,
				renderOrganizersAsCheckboxes: organizersResponse?.meta && organizersResponse.meta.total < 15,
				regions: regionsResponse?.data,
				regionsIsLoading,
				renderRegionsAsCheckboxes: regionsResponse?.meta && regionsResponse.meta.total < 15,
				tags: tagsResponse?.data,
				tagsIsLoading,
				renderTagsAsCheckboxes: tagsResponse?.meta && tagsResponse.meta.total < 15,
				targetGroups: targetGroupsResponse?.data,
				targetGroupsIsLoading,
				renderTargetGroupsAsCheckboxes: targetGroupsResponse?.meta && targetGroupsResponse.meta.total < 15,
			}}
		>
			{children}
		</EventFilterAccordionItemsContext.Provider>
	);
}

export function useEventFilterAccordionItems() {
	const ctx = useContext(EventFilterAccordionItemsContext);

	if (ctx === undefined) {
		throw new Error("useEventFilterAccordionItems needs to be wrapped inside an EventFilterAccordionItemsProvider");
	}

	return ctx;
}
